import {
	PropsWithChildren,
	Dispatch,
	SetStateAction,
	Children,
	CSSProperties,
	ReactElement,
	cloneElement,
	isValidElement,
	useRef,
	useState,
	useCallback,
	useContext,
	useLayoutEffect
} from 'react';
import { Tooltip } from '@components';
import { TOOLTIP_POSITION } from '@types';
import styles from './PagedDialog.module.scss';
import { PagedDialogContext } from './Context';

type TMenuItemProps = {
	label: JSX.Element | string;
	id: number;
	context?: {
		activeMenu: number;
		setActiveMenu: Dispatch<SetStateAction<number>>;
		activeSubMenu: number;
		setActiveSubMenu: Dispatch<SetStateAction<number>>;
	}
	subItem?: boolean;
	parentId?: number;
	disabled?: boolean;
	disabledTooltip?: string;
}

export const MenuItem = (props: PropsWithChildren<TMenuItemProps>) => {
	const { label, id, children, subItem, parentId, disabled, disabledTooltip } = props;
	const context = useContext(PagedDialogContext);
	const subMenuRef = useRef<HTMLDivElement>(null);
	const [subMenuHeight, setSubMenuHeight] = useState(0);

	const onMenuClick = (id: number) => {
		if (disabled) return;
		if (context) {
			if (subItem) {
				context.setActiveSubMenu(id);
			} else {
				if (context.activeMenu !== id) context.setActiveSubMenu(0);
				context.setActiveMenu(id);
			}
		}
	}
	const isActiveMenu = useCallback(() => {
		if (context) {
			if (subItem) return (context.activeMenu === parentId && context.activeSubMenu === id) ? styles.activeSubMenu : '';
			return context.activeMenu === id ? styles.active : ''
		}
		return '';
	}, [context, parentId]);
	const getStyles = () => {
		const styles: CSSProperties = {};
		if (context && context.activeMenu === id) {
			styles.maxHeight = subMenuHeight + 1;
			styles.opacity = 1;
			styles.marginTop = 0;
		} else {
			styles.maxHeight = 0;
			styles.opacity = 0;
			styles.marginTop = '-8px';
			styles.zIndex = '-10';
		}
		return styles;
	}
	useLayoutEffect(() => {
		if (subMenuRef.current) {
			setSubMenuHeight(subMenuRef.current.scrollHeight);
		}
	});
	const button = (
		<button
			className={`${styles.menuItem} ${isActiveMenu()} ${disabled ? styles.disabled : ''}`}
			onClick={() => onMenuClick(id)}
			data-testid={`@ofload-paged-dialog-menu-item${subItem ? '-subitem' : ''}`}
		>
			{label}
		</button>
	);
	return (
		<>
			{disabled && disabledTooltip
				? <Tooltip position={TOOLTIP_POSITION.RIGHT} text={disabledTooltip}>{button}</Tooltip>
				: button}
			{!disabled && Children.count(children) > 1 && <div ref={subMenuRef} className={styles.subMenu} style={getStyles()}>
				{Children.map(children, (child) => (
					isValidElement(child) ? cloneElement(child as ReactElement<TMenuItemProps>, { parentId: id }) : child
				))}
			</div>}
		</>
	);
}
MenuItem.displayName = 'MenuItem';
